import cssColorMap from './css-color-names.json';
import xkcdColorData from './xkcd-colors.json';

export type NamedColorSource = 'css' | 'xkcd';

export interface NamedColorEntry {
  id: string;
  name: string;
  displayName: string;
  hex: string;
  source: NamedColorSource;
  tokens: string[];
  aliases: string[];
}

export interface NamedColorSearchResult {
  entry: NamedColorEntry;
  score: number;
  exact: boolean;
}

export interface NamedColorSearchOptions {
  sources?: NamedColorSource[];
  limit?: number;
}

interface XkcdColorFile {
  colors: { color: string; hex: string }[];
}

const CSS_COLORS = cssColorMap as Record<string, string>;
const XKCD_COLORS = (xkcdColorData as XkcdColorFile).colors;

const DEFAULT_LIMIT = 20;

export function normaliseNamedColor(value: string): string {
  return value.trim().toLowerCase();
}

function toDisplayName(raw: string): string {
  const cleaned = raw.replace(/[_-]+/g, ' ');
  return cleaned
    .split(/\s+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
    .replace(/\b(Oklch|Rgb|Cmyk|Lab|Lch|Xyz|Hex)\b/gi, (match) => match.toUpperCase());
}

function deriveAliases(name: string): string[] {
  const aliases: string[] = [];
  if (name.includes('grey')) {
    aliases.push(name.replace(/grey/g, 'gray'));
  } else if (name.includes('gray')) {
    aliases.push(name.replace(/gray/g, 'grey'));
  }
  return aliases.map(normaliseNamedColor);
}

function buildTokens(name: string, aliases: string[]): string[] {
  const bucket = new Set<string>();
  const register = (value: string) => {
    const normalized = normaliseNamedColor(value);
    if (!normalized) return;
    bucket.add(normalized);
    normalized.split(/\s+/).forEach((token) => {
      if (token) {
        bucket.add(token);
      }
    });
  };

  register(name);
  aliases.forEach(register);

  return Array.from(bucket);
}

function ensureHex(value: string): string {
  const prefixed = value.startsWith('#') ? value : `#${value}`;
  return prefixed.toUpperCase();
}

const cssNamedColorsInternal: NamedColorEntry[] = Object.entries(CSS_COLORS).map(([rawName, hex]) => {
  const name = normaliseNamedColor(rawName);
  const aliases = deriveAliases(name);
  const displayName = toDisplayName(rawName);
  return {
    id: `css:${name}`,
    name,
    displayName,
    hex: ensureHex(hex),
    source: 'css',
    aliases,
    tokens: buildTokens(displayName, aliases),
  } satisfies NamedColorEntry;
});

const xkcdNamedColorsInternal: NamedColorEntry[] = XKCD_COLORS.map(({ color, hex }) => {
  const name = normaliseNamedColor(color);
  const displayName = toDisplayName(color);
  return {
    id: `xkcd:${name}`,
    name,
    displayName,
    hex: ensureHex(hex),
    source: 'xkcd',
    aliases: [],
    tokens: buildTokens(displayName, []),
  } satisfies NamedColorEntry;
});

export const cssNamedColors = cssNamedColorsInternal;
export const xkcdNamedColors = xkcdNamedColorsInternal;

export const allNamedColors: NamedColorEntry[] = [...cssNamedColorsInternal, ...xkcdNamedColorsInternal];

export const namedColorStats = {
  css: cssNamedColorsInternal.length,
  xkcd: xkcdNamedColorsInternal.length,
  total: allNamedColors.length,
};

const namedColorById = new Map<string, NamedColorEntry>();
const namedColorByName = new Map<string, NamedColorEntry>();
const namedColorByHex = new Map<string, NamedColorEntry[]>();

function registerEntry(entry: NamedColorEntry) {
  namedColorById.set(entry.id, entry);
  const identifiers = new Set<string>([entry.name, ...entry.aliases]);
  identifiers.forEach((identifier) => {
    namedColorByName.set(identifier, entry);
  });

  const variants = namedColorByHex.get(entry.hex) ?? [];
  variants.push(entry);
  namedColorByHex.set(entry.hex, variants);
}

allNamedColors.forEach(registerEntry);

function filterBySources<T extends NamedColorEntry>(entries: T[], sources?: NamedColorSource[]): T[] {
  if (!sources || sources.length === 0) {
    return entries;
  }
  const allowed = new Set<NamedColorSource>(sources);
  return entries.filter((entry) => allowed.has(entry.source));
}

export function getNamedColorEntries(options?: { sources?: NamedColorSource[] }): NamedColorEntry[] {
  return filterBySources(allNamedColors, options?.sources);
}

export function findNamedColorByName(name: string, options?: { sources?: NamedColorSource[] }): NamedColorEntry | null {
  const normalized = normaliseNamedColor(name);
  const entry = namedColorByName.get(normalized);
  if (!entry) {
    return null;
  }
  if (options?.sources && options.sources.length > 0 && !options.sources.includes(entry.source)) {
    return null;
  }
  return entry;
}

export function findNamedColorsByHex(hex: string, options?: { sources?: NamedColorSource[] }): NamedColorEntry[] {
  const normalized = ensureHex(hex);
  const entries = namedColorByHex.get(normalized) ?? [];
  return filterBySources(entries, options?.sources);
}

export function getNamedColorById(id: string): NamedColorEntry | null {
  return namedColorById.get(id) ?? null;
}

function levenshtein(a: string, b: string): number {
  const matrix: number[][] = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));

  for (let i = 0; i <= a.length; i += 1) {
    matrix[i][0] = i;
  }
  for (let j = 0; j <= b.length; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost,
      );
    }
  }

  return matrix[a.length][b.length];
}

export function searchNamedColorEntries(
  query: string,
  options?: NamedColorSearchOptions,
): NamedColorSearchResult[] {
  const normalizedQuery = normaliseNamedColor(query);
  if (!normalizedQuery) {
    return [];
  }

  const candidates = filterBySources(allNamedColors, options?.sources);
  const results: NamedColorSearchResult[] = [];

  for (const entry of candidates) {
    let score: number | null = null;
    let exact = false;

    if (entry.name === normalizedQuery || entry.aliases.includes(normalizedQuery)) {
      score = 0;
      exact = true;
    } else if (entry.tokens.includes(normalizedQuery)) {
      score = 0.1;
    } else if (entry.name.startsWith(normalizedQuery)) {
      score = 0.2;
    } else if (entry.tokens.some((token) => token.startsWith(normalizedQuery))) {
      score = 0.3;
    } else {
      const displayLower = entry.displayName.toLowerCase();
      if (displayLower.includes(normalizedQuery)) {
        score = 0.5;
      } else {
        const distance = levenshtein(normalizedQuery, entry.name);
        const normalizedDistance = distance / Math.max(entry.name.length, normalizedQuery.length, 1);
        if (normalizedDistance <= 0.5) {
          score = 1 + normalizedDistance;
        }
      }
    }

    if (score === null) {
      continue;
    }

    results.push({ entry, score, exact });
  }

  results.sort((a, b) => {
    if (a.score !== b.score) {
      return a.score - b.score;
    }
    if (a.exact !== b.exact) {
      return a.exact ? -1 : 1;
    }
    if (a.entry.source !== b.entry.source) {
      return a.entry.source.localeCompare(b.entry.source);
    }
    return a.entry.displayName.localeCompare(b.entry.displayName);
  });

  const limit = options?.limit ?? DEFAULT_LIMIT;
  return results.slice(0, limit);
}

